/**
 * Mock Scheduled Jobs Data
 *
 * Example jobs for testing and demonstration.
 */

import { ScheduledJob, JobExecution, JobTypeDefinition } from '../types/scheduledJobs';

// ============================================
// DATE HELPERS
// ============================================
const now = new Date();
const daysAgo = (days: number) => {
  const date = new Date(now);
  date.setDate(date.getDate() - days);
  return date.toISOString();
};
const daysFromNow = (days: number) => {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return date.toISOString();
};
const monthsAgo = (months: number) => {
  const date = new Date(now);
  date.setMonth(date.getMonth() - months);
  return date.toISOString();
};

export const mockScheduledJobs: ScheduledJob[] = [
  {
    id: 'job_001',
    name: 'Daily Data Retention Purge',
    description: 'Automatically purge users past their retention date for compliance',
    type: 'data_retention_purge',
    schedule: {
      frequency: 'daily',
      time: '02:00',
      timezone: 'America/New_York'
    },
    configuration: {
      dryRun: false,
      notifyBeforePurge: true,
      daysBeforeWarning: 7
    },
    status: 'scheduled',
    isActive: true,
    notifications: {
      onSuccess: true,
      onFailure: true,
      recipients: ['admin@company.com']
    },
    retryPolicy: {
      enabled: true,
      maxAttempts: 3,
      retryDelayMs: 300000 // 5 minutes
    },
    lastRun: daysAgo(1).split('T')[0] + 'T02:00:00Z',
    nextRun: daysFromNow(1).split('T')[0] + 'T02:00:00Z',
    createdDate: monthsAgo(2).split('T')[0] + 'T10:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: daysAgo(5).split('T')[0] + 'T14:30:00Z',
    lastModifiedBy: 'user_admin'
  },
  {
    id: 'job_002',
    name: 'Weekly Sales Report',
    description: 'Generate and email weekly sales report every Monday morning',
    type: 'scheduled_report',
    schedule: {
      frequency: 'weekly',
      time: '08:00',
      daysOfWeek: [1], // Monday
      timezone: 'America/New_York'
    },
    configuration: {
      reportType: 'sales_summary',
      format: 'pdf',
      includeCharts: true
    },
    status: 'scheduled',
    isActive: true,
    notifications: {
      onSuccess: false,
      onFailure: true,
      recipients: ['sales@company.com', 'manager@company.com']
    },
    retryPolicy: {
      enabled: true,
      maxAttempts: 2,
      retryDelayMs: 600000 // 10 minutes
    },
    lastRun: daysAgo(7).split('T')[0] + 'T08:00:00Z',
    nextRun: daysFromNow(1).split('T')[0] + 'T08:00:00Z',
    createdDate: monthsAgo(1).split('T')[0] + 'T09:00:00Z',
    createdBy: 'user_sales_manager',
    lastModifiedDate: daysAgo(7).split('T')[0] + 'T11:20:00Z',
    lastModifiedBy: 'user_admin'
  },
  {
    id: 'job_003',
    name: 'Monthly Data Cleanup',
    description: 'Archive old records and clean up temporary data',
    type: 'data_cleanup',
    schedule: {
      frequency: 'monthly',
      dayOfMonth: 1,
      time: '03:00',
      timezone: 'America/New_York'
    },
    configuration: {
      archiveOlderThan: 90, // days
      deleteTemporaryFiles: true,
      compressArchives: true
    },
    status: 'scheduled',
    isActive: true,
    notifications: {
      onSuccess: true,
      onFailure: true,
      recipients: ['admin@company.com']
    },
    retryPolicy: {
      enabled: false,
      maxAttempts: 1,
      retryDelayMs: 0
    },
    lastRun: daysAgo(12).split('T')[0] + 'T03:00:00Z',
    nextRun: daysFromNow(20).split('T')[0] + 'T03:00:00Z',
    createdDate: monthsAgo(2).split('T')[0] + 'T16:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: monthsAgo(1).split('T')[0] + 'T10:45:00Z',
    lastModifiedBy: 'user_admin'
  },
  {
    id: 'job_004',
    name: 'Payment Reminder Emails',
    description: 'Send automated payment reminders to clients with overdue invoices',
    type: 'reminder_email',
    schedule: {
      frequency: 'daily',
      time: '10:00',
      timezone: 'America/New_York'
    },
    configuration: {
      daysOverdue: 7,
      reminderTemplate: 'payment_reminder',
      includeLateFee: true
    },
    status: 'paused',
    isActive: false,
    notifications: {
      onSuccess: false,
      onFailure: true,
      recipients: ['finance@company.com']
    },
    retryPolicy: {
      enabled: true,
      maxAttempts: 3,
      retryDelayMs: 180000 // 3 minutes
    },
    lastRun: daysAgo(3).split('T')[0] + 'T10:00:00Z',
    nextRun: undefined,
    createdDate: monthsAgo(3).split('T')[0] + 'T12:00:00Z',
    createdBy: 'user_finance',
    lastModifiedDate: daysAgo(3).split('T')[0] + 'T15:30:00Z',
    lastModifiedBy: 'user_finance'
  },
  {
    id: 'job_005',
    name: 'Daily Analytics Aggregation',
    description: 'Aggregate daily statistics and metrics for reporting dashboards',
    type: 'analytics_aggregation',
    schedule: {
      frequency: 'daily',
      time: '01:00',
      timezone: 'America/New_York'
    },
    configuration: {
      metrics: ['revenue', 'leads', 'conversions'],
      aggregationType: 'sum',
      includeComparison: true
    },
    status: 'scheduled',
    isActive: true,
    notifications: {
      onSuccess: false,
      onFailure: true,
      recipients: ['analytics@company.com']
    },
    retryPolicy: {
      enabled: true,
      maxAttempts: 2,
      retryDelayMs: 300000
    },
    lastRun: daysAgo(1).split('T')[0] + 'T01:00:00Z',
    nextRun: daysFromNow(1).split('T')[0] + 'T01:00:00Z',
    createdDate: monthsAgo(1).split('T')[0] + 'T08:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: daysAgo(2).split('T')[0] + 'T09:15:00Z',
    lastModifiedBy: 'user_admin'
  }
];

export const mockJobExecutions: JobExecution[] = [
  {
    id: 'exec_001',
    jobId: 'job_001',
    jobName: 'Daily Data Retention Purge',
    startTime: '2025-01-20T02:00:00Z',
    endTime: '2025-01-20T02:05:23Z',
    status: 'completed',
    duration: 323000,
    recordsProcessed: 45,
    executedBy: 'system'
  },
  {
    id: 'exec_002',
    jobId: 'job_001',
    jobName: 'Daily Data Retention Purge',
    startTime: '2025-01-19T02:00:00Z',
    endTime: '2025-01-19T02:04:56Z',
    status: 'completed',
    duration: 296000,
    recordsProcessed: 38,
    executedBy: 'system'
  },
  {
    id: 'exec_003',
    jobId: 'job_002',
    jobName: 'Weekly Sales Report',
    startTime: '2025-01-13T08:00:00Z',
    endTime: '2025-01-13T08:02:14Z',
    status: 'completed',
    duration: 134000,
    recordsProcessed: 1,
    executedBy: 'system'
  },
  {
    id: 'exec_004',
    jobId: 'job_003',
    jobName: 'Monthly Data Cleanup',
    startTime: '2025-01-01T03:00:00Z',
    endTime: '2025-01-01T03:45:12Z',
    status: 'completed',
    duration: 2712000,
    recordsProcessed: 15420,
    executedBy: 'system'
  },
  {
    id: 'exec_005',
    jobId: 'job_001',
    jobName: 'Daily Data Retention Purge',
    startTime: '2025-01-18T02:00:00Z',
    endTime: '2025-01-18T02:00:45Z',
    status: 'failed',
    duration: 45000,
    recordsProcessed: 0,
    errorMessage: 'Database connection timeout',
    errorDetails: 'Failed to connect to database after 3 attempts. Connection timeout at 30s.',
    executedBy: 'system'
  },
  {
    id: 'exec_006',
    jobId: 'job_005',
    jobName: 'Daily Analytics Aggregation',
    startTime: '2025-01-20T01:00:00Z',
    endTime: '2025-01-20T01:12:34Z',
    status: 'completed',
    duration: 754000,
    recordsProcessed: 5280,
    executedBy: 'system'
  },
  {
    id: 'exec_007',
    jobId: 'job_004',
    jobName: 'Payment Reminder Emails',
    startTime: '2025-01-18T10:00:00Z',
    endTime: '2025-01-18T10:03:22Z',
    status: 'completed',
    duration: 202000,
    recordsProcessed: 12,
    executedBy: 'system'
  }
];

export const jobTypeDefinitions: JobTypeDefinition[] = [
  {
    type: 'data_retention_purge',
    label: 'Data Retention Purge',
    description: 'Automatically purge data past retention date',
    icon: 'Trash2',
    configFields: [
      {
        name: 'dryRun',
        label: 'Dry Run',
        type: 'checkbox',
        helpText: 'Preview what would be deleted without actually deleting',
        defaultValue: false
      },
      {
        name: 'notifyBeforePurge',
        label: 'Notify Before Purge',
        type: 'checkbox',
        helpText: 'Send warning emails before purging data',
        defaultValue: true
      },
      {
        name: 'daysBeforeWarning',
        label: 'Days Before Warning',
        type: 'number',
        helpText: 'Number of days before purge to send warning',
        defaultValue: 7
      }
    ]
  },
  {
    type: 'scheduled_report',
    label: 'Scheduled Report',
    description: 'Generate and distribute reports automatically',
    icon: 'FileText',
    configFields: [
      {
        name: 'reportType',
        label: 'Report Type',
        type: 'select',
        required: true,
        options: [
          { value: 'sales_summary', label: 'Sales Summary' },
          { value: 'activity_report', label: 'Activity Report' },
          { value: 'compliance_report', label: 'Compliance Report' },
          { value: 'performance_metrics', label: 'Performance Metrics' }
        ]
      },
      {
        name: 'format',
        label: 'Output Format',
        type: 'select',
        required: true,
        options: [
          { value: 'pdf', label: 'PDF' },
          { value: 'excel', label: 'Excel' },
          { value: 'csv', label: 'CSV' }
        ],
        defaultValue: 'pdf'
      },
      {
        name: 'includeCharts',
        label: 'Include Charts',
        type: 'checkbox',
        defaultValue: true
      }
    ]
  },
  {
    type: 'data_cleanup',
    label: 'Data Cleanup',
    description: 'Archive and clean up old records',
    icon: 'Archive',
    configFields: [
      {
        name: 'archiveOlderThan',
        label: 'Archive Records Older Than (days)',
        type: 'number',
        required: true,
        defaultValue: 90
      },
      {
        name: 'deleteTemporaryFiles',
        label: 'Delete Temporary Files',
        type: 'checkbox',
        defaultValue: true
      },
      {
        name: 'compressArchives',
        label: 'Compress Archives',
        type: 'checkbox',
        defaultValue: true
      }
    ]
  },
  {
    type: 'reminder_email',
    label: 'Reminder Email',
    description: 'Send automated reminder emails',
    icon: 'Mail',
    configFields: [
      {
        name: 'reminderTemplate',
        label: 'Email Template',
        type: 'select',
        required: true,
        options: [
          { value: 'payment_reminder', label: 'Payment Reminder' },
          { value: 'appointment_reminder', label: 'Appointment Reminder' },
          { value: 'follow_up', label: 'Follow-up' },
          { value: 'renewal_notice', label: 'Renewal Notice' }
        ]
      },
      {
        name: 'daysOverdue',
        label: 'Days Overdue Threshold',
        type: 'number',
        helpText: 'Only send reminders for items overdue by this many days',
        defaultValue: 7
      },
      {
        name: 'includeLateFee',
        label: 'Include Late Fee Information',
        type: 'checkbox',
        defaultValue: false
      }
    ]
  },
  {
    type: 'analytics_aggregation',
    label: 'Analytics Aggregation',
    description: 'Aggregate metrics and statistics',
    icon: 'BarChart3',
    configFields: [
      {
        name: 'aggregationType',
        label: 'Aggregation Type',
        type: 'select',
        required: true,
        options: [
          { value: 'sum', label: 'Sum' },
          { value: 'average', label: 'Average' },
          { value: 'count', label: 'Count' },
          { value: 'all', label: 'All Types' }
        ],
        defaultValue: 'sum'
      },
      {
        name: 'includeComparison',
        label: 'Include Period Comparison',
        type: 'checkbox',
        helpText: 'Compare with previous period',
        defaultValue: true
      }
    ]
  },
  {
    type: 'custom',
    label: 'Custom Job',
    description: 'User-defined automation job',
    icon: 'Settings',
    configFields: [
      {
        name: 'scriptName',
        label: 'Script Name',
        type: 'text',
        required: true,
        helpText: 'Name of the script to execute'
      },
      {
        name: 'parameters',
        label: 'Parameters',
        type: 'textarea',
        helpText: 'JSON formatted parameters'
      }
    ]
  }
];
